import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';

const MapPopup = ({ mapRef, lngLat, offset = 20, children, ...props }) => {
  const popupRef = useRef();
  const popupDOMRef = useRef();

  useEffect(() => {
    if (!mapRef.current) {
      // NOTE: this is possible since we use a ref for the map state so there's
      // no way to ensure this code runs after the ref is set (can't trigger a
      // useEffect from a ref value change). Currently we avoid this by
      // resetting the map state when DashboardMap is unmounted, but in the
      // future we may want to adjust how we manage the map ref and popup ref.

      console.error('MapPopup mounted before mapRef was set!');
      return null;
    }

    popupRef.current = new mapboxgl.Popup({
      maxWidth: '500px',
      offset, // default 20 pixels offset from the pin on the map
      className: 'map-popup',
      closeButton: false, // children should handle close button
      focusAfterOpen: false,
      closeOnClick: false, // don't close on map click - this is to allow for clicking on other farm icons to open their popup in 1 click
    })
      .setLngLat(lngLat)
      .setDOMContent(popupDOMRef.current)
      .addTo(mapRef.current);

    return () => {
      if (!popupRef.current) return;

      // Unmount the popup when the component is unmounted:
      popupRef.current.remove();

      // Set the ref value to null so that we can avoid trying to call `setLatLng` on an unmounted popup:
      popupRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want this to run on mount
  }, []);

  useEffect(() => {
    if (popupRef.current) {
      popupRef.current.setLngLat(lngLat);
    }
  }, [lngLat]);

  useEffect(() => {
    if (popupRef.current) {
      popupRef.current.setOffset(offset);
    }
  }, [offset]);

  return (
    // We render this in a hidden div so that the popup can be created and added to the map
    <div style={{ display: 'none' }}>
      <div ref={popupDOMRef} {...props}>
        {children}
      </div>
    </div>
  );
};

export default MapPopup;
